/**
 * useExportJob.js
 * ---------------
 * Universal React hook for triggering any module's background export job.
 *
 * Usage (in any list screen — Projects, Tasks, Jobs, Timesheets, etc.):
 *
 *   import { useExportJob } from '../../../hooks/useExportJob'
 *
 *   const { triggerExport, isExporting } = useExportJob({ module: 'projects' })
 *
 *   // Inside Tools dropdown:
 *   <button onClick={() => triggerExport(currentFilters)}>Download Excel</button>
 *
 * Flow:
 *  1. POST /api/jobs/  → returns jobId in < 20ms (non-blocking)
 *  2. Toast: "Export started in background…"
 *  3. Polls GET /api/jobs/{jobId} every 2 seconds
 *  4. On COMPLETED → toast with "Download" button; triggers browser download
 *  5. On FAILED    → error toast with message
 */
import { useState, useRef, useCallback } from 'react'
import { enqueueJob, getJobStatus, downloadJobFile } from '../api/jobsApi'
import { appToast } from '../components/AppToast'

const POLL_INTERVAL_MS = 2000
const MAX_POLL_ATTEMPTS = 150 // 5 minutes max polling time

export function useExportJob({ module, jobType = 'EXPORT', fileFormat = 'CSV' } = {}) {
  const [isExporting, setIsExporting] = useState(false)
  const [currentJobId, setCurrentJobId] = useState(null)
  const pollTimerRef = useRef(null)
  const pollCountRef = useRef(0)

  const _stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }
    pollCountRef.current = 0
  }, [])

  const _pollJob = useCallback(
    async (jobId, fileName) => {
      pollCountRef.current += 1

      if (pollCountRef.current > MAX_POLL_ATTEMPTS) {
        _stopPolling()
        setIsExporting(false)
        appToast.error('Export timed out. Please try again.')
        return
      }

      try {
        const job = await getJobStatus(jobId)

        if (job.status === 'COMPLETED') {
          _stopPolling()
          setIsExporting(false)
          const resolvedFileName = job.fileName || fileName || 'export.csv'
          await downloadJobFile(jobId, resolvedFileName)
          appToast.success(`Export ready! Downloading "${resolvedFileName}"`)
        } else if (job.status === 'FAILED') {
          _stopPolling()
          setIsExporting(false)
          appToast.error(`Export failed: ${job.errorMessage || 'Unknown error. Please try again.'}`)
        } else {
          // QUEUED or PROCESSING — keep polling
          pollTimerRef.current = setTimeout(() => _pollJob(jobId, fileName), POLL_INTERVAL_MS)
        }
      } catch (err) {
        console.error('[useExportJob] polling error:', err)
        // Keep polling on transient network errors, stop on client errors
        if (err?.response?.status >= 400 && err?.response?.status < 500) {
          _stopPolling()
          setIsExporting(false)
          appToast.error('Export error. Please try again.')
        } else {
          pollTimerRef.current = setTimeout(() => _pollJob(jobId, fileName), POLL_INTERVAL_MS * 2)
        }
      }
    },
    [_stopPolling],
  )

  /**
   * triggerExport(filters, overrideFormat)
   * ---------------------------------------
   * Enqueues a background export job with current screen filters and optional format override.
   * Returns immediately — polling happens asynchronously.
   *
   * @param {Object} filters          - Current screen query params / filter state
   * @param {string} [overrideFormat] - Optional format override ('CSV', 'XLSX', 'PDF')
   */
  const triggerExport = useCallback(
    async (filters = {}, overrideFormat = null) => {
      if (isExporting) {
        appToast.warning('An export is already in progress. Please wait.')
        return
      }

      const activeFormat = (overrideFormat || fileFormat || 'CSV').toUpperCase()

      try {
        setIsExporting(true)
        pollCountRef.current = 0

        const job = await enqueueJob({
          module,
          jobType,
          fileFormat: activeFormat,
          payload: filters,
        })

        const jobId = job.jobId
        setCurrentJobId(jobId)

        appToast.info(
          `Export job started (${activeFormat} Job #${jobId}). Processing in background — you can continue working!`,
        )

        // Start polling
        pollTimerRef.current = setTimeout(() => _pollJob(jobId, null), POLL_INTERVAL_MS)
      } catch (err) {
        console.error('[useExportJob] enqueue error:', err)
        setIsExporting(false)
        appToast.error('Failed to start export. Please try again.')
      }
    },
    [isExporting, module, jobType, fileFormat, _pollJob],
  )

  return {
    triggerExport,
    isExporting,
    currentJobId,
  }
}
